import { env } from "../env.js";

/**
 * Which identity provider this build talks to.
 *
 * Mirrors the `ACCESS_VALIDATOR` env flag ms-accounting uses on the backend: one
 * provider per build, switched by environment, so a cutover is atomic per
 * environment and rolling back is an env change instead of a revert.
 *
 * Defaults to Auth0: a build that says nothing keeps today's behavior.
 */
export const AUTH_PROVIDER_AUTH0 = "auth0";
export const AUTH_PROVIDER_ZITADEL = "zitadel";

export const authProvider =
  env.REACT_APP_AUTH_PROVIDER === AUTH_PROVIDER_ZITADEL
    ? AUTH_PROVIDER_ZITADEL
    : AUTH_PROVIDER_AUTH0;

export const isZitadel = authProvider === AUTH_PROVIDER_ZITADEL;

/** Claim holding the user's roles. Zitadel re-emits it under the Auth0 name on purpose. */
export const ROLES_CLAIM = env.REACT_APP_AUTH0_ROLES;

/**
 * Claim holding the legacy payments token, read straight from the ID token by
 * the movements/downloads screens.
 *
 * Only set in `env/.env.production` — dev and staging already run without it,
 * which is why the Zitadel Action not emitting it does not block a staging test.
 * It does block production: see `auth0-migration/payment-site-pilot.md`.
 */
export const LEGACY_TOKEN_CLAIM = env.REACT_APP_AUTH0_LEGACY_TOKEN;

/**
 * Authorization Code + PKCE against Zitadel. No client secret: security comes from
 * PKCE plus the redirect URIs registered in the Console.
 *
 * `authority` lets oidc-client-ts discover endpoints via
 * {authority}/.well-known/openid-configuration, so no URL is hardcoded here.
 *
 * Ported from houm-post-login/src/auth-config.ts, the probe app already proven
 * against this instance.
 */
export const zitadelConfig = {
  authority: (env.REACT_APP_ZITADEL_AUTHORITY || "").replace(/\/$/, ""),
  client_id: env.REACT_APP_ZITADEL_CLIENT_ID,
  redirect_uri: env.REACT_APP_REDIRECT_URI || `${window.location.origin}/admin`,
  post_logout_redirect_uri:
    env.REACT_APP_POST_LOGOUT_REDIRECT_URI || `${window.location.origin}/auth`,
  response_type: "code",
  scope: env.REACT_APP_ZITADEL_SCOPE || "openid profile email offline_access",
  automaticSilentRenew: true,
  // Merges the /userinfo response into `user.profile`, which is where every screen
  // reads claims from. Not optional here: Zitadel's Actions v2 `preuserinfo` and
  // `preaccesstoken` functions fill the userinfo endpoint and the access token, and
  // neither touches the ID token, so the roles ms-auth appends are invisible without
  // this. Verified against the staging instance on 2026-09-03: /userinfo answered 200
  // with `email` and `name` for a session whose ID token carried neither.
  loadUserInfo: true,
  // Strips ?code=&state= after the callback so the codes do not linger in the URL bar.
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};
